import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { UseApi } from '../Hooks/UseApi';
import { ValidateOnboardingData, GetOnboardingProgress } from '../Hooks/UseOnboarding';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

// Styled components matching your app's design
const GradientPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ff1744 30%, #ff8e53 90%)',
  color: '#fff',
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  boxShadow: '0 10px 30px rgba(255, 23, 68, 0.3)',
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '15px',
  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  background: theme.palette.background.paper,
}));

const ProgressChip = styled(Chip)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '14px',
  fontWeight: 'bold',
}));

// US State codes for validation
const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const OnboardingPage = () => {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const api = UseApi();
  const { isStudioMember, isStudioOwner } = UseAuthRoles();

  // Form state
  const [formData, setFormData] = useState({
    // User information
    firstName: '',
    lastName: '',
    // Shop information
    shopName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '' // Optional field
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // Terms acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Check if user is a studio member and pre-fill studio info
  useEffect(() => {
    if (isStudioMember || isStudioOwner) {
    }
  }, [isStudioMember, isStudioOwner]);

  // Steps for the stepper
  const steps = [
    'Personal Information',
    'Business Information', 
    'Location Details',
    'Terms & Conditions',
    'Complete Setup'
  ];

  // Update progress whenever form data or terms acceptance changes
  useEffect(() => {
    // Calculate progress based on required fields + terms acceptance
    const requiredFields = [
      formData.firstName?.trim(),
      formData.lastName?.trim(),
      formData.shopName?.trim(),
      formData.address?.trim(), 
      formData.city?.trim(),
      formData.state?.trim(),
      formData.zipCode?.trim()
    ];
    
    const completedFields = requiredFields.filter(field => field && field !== '').length;
    const totalFields = requiredFields.length + 2; // 7 form fields + 2 checkboxes = 9
    const termsCount = (termsAccepted ? 1 : 0) + (privacyAccepted ? 1 : 0);
    const newProgress = Math.round(((completedFields + termsCount) / totalFields) * 100);
    
    setProgress(newProgress);

    // Update current step based on progress (now 5 steps)
    if (completedFields < 2) setCurrentStep(0);           // Personal Info
    else if (completedFields < 3) setCurrentStep(1);      // Business Info  
    else if (completedFields < 7) setCurrentStep(2);      // Location
    else if (!termsAccepted || !privacyAccepted) setCurrentStep(3); // Terms
    else setCurrentStep(4);                               // Complete
    
    // Debug logging
    console.log('Progress Debug:', {
      completedFields,
      termsAccepted,
      privacyAccepted,
      progress: newProgress,
      currentStep
    });
  }, [formData, termsAccepted, privacyAccepted]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    // Special handling for state field - convert to uppercase
    if (field === 'state') {
      setFormData(prev => ({
        ...prev,
        [field]: value.toUpperCase()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Debug current form state
    console.log('Ã°Å¸â€Â Form submission attempt:', {
      formData,
      progress,
      isFormComplete
    });
    
    // Check if all required fields are filled (custom validation)
    const missingFields = [];
    const newValidationErrors = {};
    
    if (!formData.firstName?.trim()) missingFields.push('First Name');
    if (!formData.lastName?.trim()) missingFields.push('Last Name');
    if (!formData.shopName?.trim()) missingFields.push('Shop Name');
    if (!formData.address?.trim()) missingFields.push('Street Address');
    if (!formData.city?.trim()) missingFields.push('City');
    
    // State validation - must be 2 characters and valid US state code
    if (!formData.state?.trim()) {
      missingFields.push('State');
    } else if (formData.state.trim().length !== 2) {
      newValidationErrors.state = 'State must be 2 characters (e.g., GA, CA, NY)';
    } else if (!US_STATE_CODES.includes(formData.state.trim().toUpperCase())) {
      newValidationErrors.state = 'Please enter a valid US state code (e.g., GA, CA, NY)';
    }
    
    if (!formData.zipCode?.trim()) missingFields.push('ZIP Code');
    
    // ZIP code validation
    if (formData.zipCode?.trim() && !/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      newValidationErrors.zipCode = 'Please enter a valid ZIP code (12345 or 12345-6789)';
    }

    // Terms acceptance validation
    if (!termsAccepted) {
      missingFields.push('Terms and Conditions acceptance');
    }
    if (!privacyAccepted) {
      missingFields.push('Privacy Policy acceptance');
    }
    
    if (missingFields.length > 0 || Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      if (missingFields.length > 0) {
        setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      } else {
        setError('Please fix the validation errors above.');
      }
      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Save user profile first (including terms acceptance)
      const userProfileData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: user?.email || '', // Keep existing email from Auth0
        // Terms acceptance tracking
        terms_accepted_at: new Date().toISOString(),
        terms_version: 'December 17, 2025', // Match Artist-Terms.js effectiveDate
        privacy_accepted_at: new Date().toISOString(),
        privacy_version: 'December 17, 2025'
      };
      await api.fetchWithAuth('/user/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userProfileData)
      });

      // Save shop profile
      const shopProfileData = {
        name: formData.shopName.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(), // Ensure uppercase
        zip_code: formData.zipCode.trim(),
        phone: formData.phone.trim() || '(000) 000-0000', // Provide default if empty
        email: user?.email || '' // Use Auth0 email for shop email
      };
      // The /shop/profile/ endpoint has built-in studio sync logic
      await api.fetchWithAuth('/shop/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shopProfileData)
      });
      setSuccess(true);

      // Redirect to dashboard after brief success message
      // Users reaching onboarding already have a subscription, so go directly to dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

    } catch (error) {
      console.error('Ã¢ÂÅ’ Onboarding submission error:', error);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to save your information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormComplete = progress === 100 && termsAccepted && privacyAccepted;

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <GradientPaper>
          <CheckCircleIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Welcome to InkedUp Manager!
          </Typography>
          <Typography variant="body1">
            Your business profile has been created successfully. 
            Redirecting you to your dashboard...
          </Typography>
          <CircularProgress sx={{ mt: 3, color: 'white' }} />
        </GradientPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <GradientPaper>
        <BusinessIcon sx={{ fontSize: 50, mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Welcome to InkedUp Manager!
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
          Let's set up your business profile
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Hi {user?.given_name || user?.name?.split(' ')[0] || 'there'}! We need some basic information 
          about your tattoo shop to get you started.
        </Typography>
      </GradientPaper>

      {/* Progress Indicator */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <ProgressChip 
            label={`${progress}% Complete`} 
            color={progress === 100 ? 'success' : 'primary'} 
            icon={progress === 100 ? <CheckCircleIcon /> : undefined}
          />
          <Typography variant="body2" color="text.secondary">
            Step {currentStep + 1} of {steps.length}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'rgba(255, 23, 68, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: progress === 100 ? '#4caf50' : '#ff1744'
            }
          }} 
        />
      </Box>

      {/* Stepper */}
      <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Form */}
      <FormPaper>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            
            {/* Personal Information Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountCircle sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">
                  Personal Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName || 'Your first name'}
                placeholder="John"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName || 'Your last name'}
                placeholder="Smith"
              />
            </Grid>
            
            {/* Business Information Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">
                  Business Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isStudioMember || isStudioOwner ? "Studio/Shop Name *" : "Shop Name *"}
                value={formData.shopName}
                onChange={handleChange('shopName')}
                error={!!validationErrors.shopName}
                helperText={
                  isStudioMember || isStudioOwner 
                    ? "Enter your studio name - this will be shared with all studio members"
                    : (validationErrors.shopName || 'What should we call your tattoo shop?')
                }
                placeholder={
                  isStudioMember || isStudioOwner 
                    ? "e.g., Ink Masters Studio" 
                    : "e.g., Ink Masters Studio"
                }
              />
              {(isStudioMember || isStudioOwner) && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={isStudioOwner ? "Studio Owner" : "Studio Member"} 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Studio name will be synced across all team members
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone || 'Optional - leave blank to use (000) 000-0000'}
                placeholder="(555) 123-4567"
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">
                  Shop Location
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address *"
                value={formData.address}
                onChange={handleChange('address')}
                error={!!validationErrors.address}
                helperText={validationErrors.address || 'Your shop\'s street address'}
                placeholder="123 Main Street"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City *"
                value={formData.city}
                onChange={handleChange('city')}
                error={!!validationErrors.city}
                helperText={validationErrors.city}
                placeholder="Your City"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="State *"
                value={formData.state}
                onChange={handleChange('state')}
                error={!!validationErrors.state}
                helperText={validationErrors.state || 'Two-letter code (e.g., GA, CA, NY)'}
                placeholder="GA"
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code *"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                error={!!validationErrors.zipCode}
                helperText={validationErrors.zipCode}
                placeholder="12345"
              />
            </Grid>

            {/* Terms and Conditions Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ 
                p: 3, 
                border: '1px solid', 
                borderColor: (!termsAccepted || !privacyAccepted) ? 'warning.main' : 'success.main',
                borderRadius: 2,
                backgroundColor: 'background.default'
              }}>
                <Typography variant="h6" color="primary.main" gutterBottom>
                  Terms & Conditions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please review and accept our terms to complete your account setup.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the{' '}
                      <Link
                        component={RouterLink}
                        to="/legal/artist-terms"
                        target="_blank"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Terms and Conditions
                      </Link>
                      {' '}for Artists & Shops *
                    </Typography>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the{' '}
                      <Link
                        component={RouterLink}
                        to="/legal/privacy-policy"
                        target="_blank"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Privacy Policy
                      </Link>
                      {' '}*
                    </Typography>
                  }
                />

                {(!termsAccepted || !privacyAccepted) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    You must accept both agreements to complete your setup.
                  </Alert>
                )}
              </Box>
            </Grid>

            {/* Error Display */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || !isFormComplete}
                  sx={{
                    minWidth: 200,
                    py: 1.5,
                    borderRadius: '25px',
                    background: isFormComplete 
                      ? 'linear-gradient(45deg, #ff1744 30%, #ff8e53 90%)'
                      : undefined,
                    '&:hover': {
                      background: isFormComplete 
                        ? 'linear-gradient(45deg, #d50000 30%, #ff6f00 90%)'
                        : undefined,
                    }
                  }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                >
                  {loading ? 'Setting Up...' : progress === 100 ? 'Complete Setup' : `Complete Setup (${progress}% done)`}
                </Button>
              </Box>
            </Grid>

            {/* Help Text */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                This information helps us set up your account and enables features like client management and scheduling.
                You can always update this information later in your settings.
              </Typography>
            </Grid>

          </Grid>
        </form>
      </FormPaper>
    </Container>
  );
};

export default OnboardingPage;